import React, { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Users, StickyNote, History, Target, Check, Shield,
  Search, Layers3, SlidersHorizontal,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { EmptyState, SectionTitle } from "@/components/askilia/layout-bits";
import {
  StageBadge, OwnershipBadge, ChannelBadge, SourceTag, BookingStateBadge, Chip,
  AttendanceBadge,
} from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { money, fmtTime, initials } from "@/lib/format";
import SegmentsWorkspace from "@/pages/opportunities/SegmentsWorkspace";
import { useI18n } from "@/i18n";

const OPEN_STAGES = ["New", "Qualifying", "Qualified", "Offered", "Booked/Won"];
const CLOSED = ["Not now", "Lost", "Disqualified"];

function relativeTime(value, t, formatNumber) {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return t("operations.time.justNow");
  if (minutes < 60) return t("operations.time.minutesAgo", { count: formatNumber(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("operations.time.hoursAgo", { count: formatNumber(hours) });
  return t("operations.time.daysAgo", { count: formatNumber(Math.floor(hours / 24)) });
}

export default function Customers() {
  const { state } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const params = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const active = params.id ? state.customers.find((c) => c.id === params.id) : null;
  const view = !active && searchParams.get("view") === "segments" ? "segments" : "people";
  const segments = useMemo(() => state.segments || [], [state.segments]);

  const filteredCustomers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const selectedSegment = segments.find((segment) => segment.id === segmentFilter);
    return state.customers.filter((customer) => {
      const matchesQuery = !normalizedQuery
        || `${customer.name} ${localize(customer.name)} ${customer.phone} ${customer.email} ${customer.qualification.map((field) => `${field.value} ${localize(field.value)}`).join(" ")}`.toLowerCase().includes(normalizedQuery);
      const matchesStage = stageFilter === "all" || customer.stage === stageFilter;
      const matchesSegment = !selectedSegment || selectedSegment.memberIds.includes(customer.id);
      return matchesQuery && matchesStage && matchesSegment;
    });
  }, [localize, query, segmentFilter, segments, stageFilter, state.customers]);

  const changeView = (nextView) => {
    nav(nextView === "segments" ? "/customers?view=segments" : "/customers");
  };

  if (view === "segments") {
    return (
      <div className="h-full overflow-y-auto bg-canvas scroll-thin">
        <div className="border-b border-border bg-surface px-4 py-4 lg:px-8">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground">{t("opportunities.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("opportunities.segmentsDescription")}</p>
            </div>
            <OpportunityViewSwitch value="segments" onChange={changeView} />
          </div>
        </div>
        <SegmentsWorkspace onViewCustomer={(customerId) => nav(`/customers/${customerId}`)} />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className={`w-full flex-col border-r border-border bg-surface lg:flex lg:w-[440px] ${active ? "hidden lg:flex" : "flex"}`}>
        <div className="space-y-3 border-b border-border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold tracking-tight">{t("opportunities.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("opportunities.peopleDescription")}</p>
            </div>
            <OpportunityViewSwitch value="people" onChange={changeView} />
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("opportunities.searchPlaceholder")} aria-label={t("opportunities.searchAria")} className="bg-background pl-9" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="bg-background" aria-label={t("opportunities.stageFilterAria")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("opportunities.allStages")}</SelectItem>
                {[...OPEN_STAGES, ...CLOSED].map((stage) => <SelectItem key={stage} value={stage}>{localize(stage)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={segmentFilter} onValueChange={setSegmentFilter}>
              <SelectTrigger className="bg-background" aria-label={t("opportunities.segmentFilterAria")}><SelectValue placeholder={t("opportunities.allSegments")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("opportunities.allSegments")}</SelectItem>
                {segments.map((segment) => <SelectItem key={segment.id} value={segment.id}>{localize(segment.name)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin">
          {filteredCustomers.map((c) => (
            <button
              key={c.id}
              onClick={() => nav(`/customers/${c.id}`)}
              className={"flex w-full items-start gap-3 border-b border-border/70 p-3.5 text-left transition-colors " + (active?.id === c.id ? "bg-primary-soft/50" : "hover:bg-muted/60")}
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">{initials(localize(c.name))}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-sm font-medium text-foreground">{localize(c.name)}</p>
                    <span className="flex shrink-0 items-center -space-x-0.5" aria-label={t("opportunities.channelsAria", { channels: c.channels.map((channel) => localize(channel.type)).join(", ") })}>
                      {c.channels.map((channel, index) => (
                        <ChannelBadge key={`${channel.type}-${index}`} channel={channel.type} showLabel={false} className="ring-2 ring-surface" />
                      ))}
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(c.lastInteraction, t, formatNumber)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2 text-xs">
                  <p className="truncate text-muted-foreground">{localize(c.qualification[0]?.value) || t("opportunities.interestNotSet")}</p>
                  <p className="shrink-0 font-semibold text-foreground">{t("opportunities.potentialValue", { value: money(c.value) })}</p>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <StageBadge stage={c.stage} />
                  {c.nextAction ? <Chip tone="warning">{localize(c.nextAction.label)}</Chip> : <Chip tone="muted">{t("opportunities.noNextAction")}</Chip>}
                </div>
              </div>
            </button>
          ))}
          {!filteredCustomers.length && (
            <div className="px-6 py-12 text-center">
              <SlidersHorizontal className="mx-auto h-5 w-5 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium text-foreground">{t("opportunities.emptyFiltered")}</p>
              <button className="mt-1 text-xs font-medium text-primary hover:underline" onClick={() => { setQuery(""); setStageFilter("all"); setSegmentFilter("all"); }}>{t("opportunities.clearFilters")}</button>
            </div>
          )}
        </div>
      </div>

      {active ? (
        <CustomerDetail key={active.id} cust={active} />
      ) : (
        <div className="hidden flex-1 items-center justify-center lg:flex">
          <EmptyState icon={Users} title={t("opportunities.select.title")} description={t("opportunities.select.description")} />
        </div>
      )}
    </div>
  );
}

function OpportunityViewSwitch({ value, onChange }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex h-9 shrink-0 items-center rounded-lg bg-muted p-1" aria-label={t("opportunities.viewAria")}>
      <button
        className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${value === "people" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
        aria-pressed={value === "people"}
        onClick={() => onChange("people")}
      >
        <Users className="h-3.5 w-3.5" /> {t("opportunities.people")}
      </button>
      <button
        className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${value === "segments" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
        aria-pressed={value === "segments"}
        onClick={() => onChange("segments")}
      >
        <Layers3 className="h-3.5 w-3.5" /> {t("opportunities.segments")}
      </button>
    </div>
  );
}

function CustomerDetail({ cust }) {
  const { state, actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const nav = useNavigate();
  const [note, setNote] = useState("");
  const [closeStage, setCloseStage] = useState(null);
  const [reason, setReason] = useState("");
  const [naOpen, setNaOpen] = useState(false);
  const [naLabel, setNaLabel] = useState("");

  const bookings = state.bookings.filter((b) => b.customerId === cust.id).sort((a, b) => new Date(b.start) - new Date(a.start));
  const convs = state.conversations.filter((c) => c.customerId === cust.id);
  const relIds = new Set([cust.id, ...bookings.map((b) => b.id), ...convs.map((c) => c.id)]);
  const audit = state.activity.filter((a) => relIds.has(a.entity));

  const timeline = [
    ...bookings.map((b) => ({ ts: b.start, kind: "booking", data: b })),
    ...audit.map((a) => ({ ts: a.ts, kind: "audit", data: a })),
    ...cust.notes.map((n) => ({ ts: n.ts, kind: "note", data: n })),
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  const changeStage = (stage) => {
    if (CLOSED.includes(stage)) { setCloseStage(stage); return; }
    actions.setStage(cust.id, stage);
  };

  return (
    <div className="flex-1 overflow-y-auto scroll-thin bg-canvas">
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 lg:px-8">
        <button onClick={() => nav("/customers")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground lg:hidden">
          <ArrowLeft className="h-4 w-4" /> {t("opportunities.allOpportunities")}
        </button>

        {/* header */}
        <div className="rounded-xl border border-border bg-surface p-5 shadow-xs rosehip-glow">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-lg font-semibold text-secondary-foreground">{initials(localize(cust.name))}</span>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">{localize(cust.name)}</h1>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {cust.channels.map((ch) => <ChannelBadge key={ch.type} channel={ch.type} />)}
                  <OwnershipBadge owner={cust.owner} />
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t("opportunities.value")}</p>
              <p className="text-2xl font-semibold text-foreground">{money(cust.value)}</p>
            </div>
          </div>
          {cust.mergePending && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-human/30 bg-human-soft px-3 py-2 text-xs text-human-soft-foreground">
              <Shield className="h-4 w-4" /> {t("opportunities.duplicateNotice", { channel: localize(cust.mergePending.channel), reason: localize(cust.mergePending.reason) })}
              <button className="ml-auto whitespace-nowrap font-semibold underline">{t("opportunities.confirmMerge")}</button>
            </div>
          )}
        </div>

        {/* lifecycle + next action */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
            <SectionTitle>{t("opportunities.lifecycle")}</SectionTitle>
            <div className="mb-3 flex flex-wrap gap-1">
              {OPEN_STAGES.map((s, i) => {
                const idx = OPEN_STAGES.indexOf(cust.stage);
                const done = idx >= i && idx >= 0;
                return (
                  <div key={s} className={"flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium " + (done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    {done && <Check className="h-3 w-3" />} {localize(s)}
                  </div>
                );
              })}
            </div>
            <Select value={CLOSED.includes(cust.stage) ? cust.stage : cust.stage} onValueChange={changeStage}>
              <SelectTrigger><SelectValue placeholder={t("opportunities.changeStage")} /></SelectTrigger>
              <SelectContent>
                {OPEN_STAGES.map((s) => <SelectItem key={s} value={s}>{localize(s)}</SelectItem>)}
                <SelectItem value="Not now">{localize("Not now")}</SelectItem>
                <SelectItem value="Lost">{localize("Lost")}</SelectItem>
                <SelectItem value="Disqualified">{localize("Disqualified")}</SelectItem>
              </SelectContent>
            </Select>
            {cust.closeReason && <p className="mt-2 text-xs text-muted-foreground">{t("opportunities.reason", { reason: localize(cust.closeReason) })}</p>}
          </div>

          <div className="flex flex-col rounded-xl border border-border bg-surface p-4 shadow-xs">
            <SectionTitle>{t("opportunities.nextAction.title")}</SectionTitle>
            {cust.nextAction ? (
              <div className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning-soft-foreground">
                <p className="font-medium">{localize(cust.nextAction.label)}</p>
                <p className="mt-0.5 text-xs">{t("opportunities.nextAction.dueOwner", { due: localize(cust.nextAction.due), owner: localize(cust.nextAction.owner) })}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("opportunities.nextAction.none")}</p>
            )}
            <div className="mt-auto flex gap-2 pt-3">
              <Button size="sm" variant="outline" onClick={() => setNaOpen(true)}><Target className="mr-1.5 h-4 w-4" /> {t("opportunities.nextAction.set")}</Button>
              {cust.nextAction && <Button size="sm" variant="ghost" onClick={() => actions.setNextAction(cust.id, null)}>{t("opportunities.nextAction.clear")}</Button>}
            </div>
          </div>
        </div>

        {/* qualification */}
        <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
          <SectionTitle hint={t("opportunities.qualificationHint")}>{t("opportunities.qualification")}</SectionTitle>
          <div className="grid gap-3 sm:grid-cols-2">
            {cust.qualification.map((f, i) => (
              <div key={i} className="flex items-start justify-between gap-2 rounded-lg border border-border/70 bg-background p-3">
                <div>
                  <p className="text-[11px] text-muted-foreground">{localize(f.key)}</p>
                  <p className="text-sm font-medium text-foreground">{localize(f.value)}</p>
                </div>
                <SourceTag source={f.source} />
              </div>
            ))}
            {cust.ageBand ? (
              <div className="flex items-start justify-between gap-2 rounded-lg border border-border/70 bg-background p-3">
                <div>
                  <p className="text-[11px] text-muted-foreground">{t("opportunities.ageBand")}</p>
                  <p className="text-sm font-medium text-foreground">{localize(cust.ageBand)}</p>
                </div>
                <SourceTag source="imported" />
              </div>
            ) : null}
          </div>
          {cust.insurance && <p className="mt-3 text-xs text-muted-foreground">{t("opportunities.insurance")}: <span className="font-medium text-foreground">{localize(cust.insurance)}</span></p>}
        </div>

        <CustomerSegments cust={cust} />

        {/* timeline */}
        <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
          <SectionTitle hint={t("opportunities.activityHint")}><History className="mr-1.5 inline h-4 w-4" /> {t("opportunities.activity")}</SectionTitle>
          {timeline.length === 0 ? <p className="text-sm text-muted-foreground">{t("opportunities.noActivity")}</p> : (
            <ol className="relative space-y-4 border-l border-border pl-5">
              {timeline.map((ev, i) => <TimelineItem key={i} ev={ev} state={state} />)}
            </ol>
          )}
        </div>

        {/* notes */}
        <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
          <SectionTitle><StickyNote className="mr-1.5 inline h-4 w-4" /> {t("opportunities.notes.title")}</SectionTitle>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("opportunities.notes.placeholder")} className="resize-none bg-background" />
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={!note.trim()} onClick={() => { actions.addNote(cust.id, note.trim()); setNote(""); }}>{t("opportunities.notes.save")}</Button>
          </div>
          <div className="mt-3 space-y-2">
            {cust.notes.map((n) => (
              <div key={n.id} className="rounded-lg border border-border/70 bg-background p-3 text-sm">
                <p className="text-foreground">{localize(n.text)}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{localize(n.by)} · {relativeTime(n.ts, t, formatNumber)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* close reason dialog */}
      <Dialog open={!!closeStage} onOpenChange={(o) => !o && setCloseStage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("opportunities.close.moveTo", { stage: localize(closeStage) })}</DialogTitle>
            <DialogDescription>{t("opportunities.close.description")}</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={t("opportunities.close.placeholder")} className="bg-background" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseStage(null)}>{t("opportunities.actions.cancel")}</Button>
            <Button disabled={!reason.trim()} onClick={() => { actions.setStage(cust.id, closeStage, reason.trim()); setCloseStage(null); setReason(""); }}>{t("opportunities.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* next action dialog */}
      <Dialog open={naOpen} onOpenChange={setNaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("opportunities.nextAction.dialogTitle")}</DialogTitle></DialogHeader>
          <Input value={naLabel} onChange={(e) => setNaLabel(e.target.value)} placeholder={t("opportunities.nextAction.placeholder")} className="bg-background" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNaOpen(false)}>{t("opportunities.actions.cancel")}</Button>
            <Button disabled={!naLabel.trim()} onClick={() => { actions.setNextAction(cust.id, { label: naLabel.trim(), due: "today", owner: state.user.name }); setNaOpen(false); setNaLabel(""); }}>{t("opportunities.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomerSegments({ cust }) {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const segments = state.segments || [];
  const memberships = segments.filter((segment) => segment.memberIds.includes(cust.id));

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) setSelectedIds(memberships.map((segment) => segment.id));
    setOpen(nextOpen);
  };

  const toggle = (segmentId, checked) => {
    setSelectedIds((current) => checked
      ? Array.from(new Set([...current, segmentId]))
      : current.filter((id) => id !== segmentId));
  };

  const save = () => {
    actions.setCustomerSegments(cust.id, selectedIds);
    setOpen(false);
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
      <SectionTitle
        hint={t("opportunities.customerSegments.hint")}
        right={<Button size="sm" variant="outline" onClick={() => handleOpenChange(true)}>{t("opportunities.customerSegments.manage")}</Button>}
      >
        {t("opportunities.segments")}
      </SectionTitle>
      {memberships.length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {memberships.map((segment) => (
            <div key={segment.id} className="rounded-lg border border-border/70 bg-background p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-foreground">{localize(segment.name)}</p>
                <SourceTag source={segment.source} />
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{localize(segment.memberEvidence?.[cust.id]) || t("opportunities.customerSegments.addedByTeam")}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("opportunities.customerSegments.empty")}</p>
      )}
      <button className="mt-3 text-xs font-medium text-primary hover:underline" onClick={() => nav("/customers?view=segments")}>{t("opportunities.customerSegments.openWorkspace")}</button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("opportunities.customerSegments.dialogTitle", { name: localize(cust.name) })}</DialogTitle>
            <DialogDescription>{t("opportunities.customerSegments.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] divide-y divide-border overflow-y-auto rounded-lg border border-border scroll-thin">
            {segments.map((segment) => {
              const checked = selectedIds.includes(segment.id);
              return (
                <label key={segment.id} className={`flex items-start gap-3 p-3 ${segment.membershipLocked ? "cursor-default bg-muted/20" : "cursor-pointer hover:bg-muted/50"}`}>
                  <Checkbox className="mt-0.5" checked={checked} disabled={segment.membershipLocked} onCheckedChange={(value) => toggle(segment.id, Boolean(value))} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{localize(segment.name)}</span>
                      <SourceTag source={segment.source} />
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {localize(segment.description)}{segment.membershipLocked ? ` ${t("opportunities.customerSegments.lockedNote")}` : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("opportunities.actions.cancel")}</Button>
            <Button onClick={save}>{t("opportunities.customerSegments.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TimelineItem({ ev, state }) {
  const { t, localize, formatDate, formatNumber } = useI18n();
  const dot = { booking: "bg-primary", audit: "bg-human", note: "bg-accent" }[ev.kind];
  return (
    <li className="relative">
      <span className={"absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-surface " + dot} />
      {ev.kind === "booking" && (() => {
        const svc = state.services.find((s) => s.id === ev.data.serviceId);
        return (
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{localize(svc?.name)}</p>
              <BookingStateBadge state={ev.data.state} />
              <AttendanceBadge booking={ev.data} compact />
            </div>
            <p className="text-xs text-muted-foreground">{formatDate(ev.data.start, { weekday: "short", month: "short", day: "numeric" })} {fmtTime(ev.data.start)} · {money(ev.data.price)} · {ev.data.source === "ilia" ? t("opportunities.timeline.bookedByIlia") : t("opportunities.timeline.bookedByPerson")}</p>
          </div>
        );
      })()}
      {ev.kind === "audit" && (
        <div>
          <p className="text-sm text-foreground">{localize(ev.data.action)}</p>
          <p className="text-xs text-muted-foreground">{localize(ev.data.detail)} · <span>{localize(ev.data.actor)}</span> · {relativeTime(ev.data.ts, t, formatNumber)}</p>
        </div>
      )}
      {ev.kind === "note" && (
        <div>
          <p className="text-sm text-foreground">{localize(ev.data.text)}</p>
          <p className="text-xs text-muted-foreground">{t("opportunities.timeline.note")} · {localize(ev.data.by)} · {relativeTime(ev.data.ts, t, formatNumber)}</p>
        </div>
      )}
    </li>
  );
}
